/**
 * carouselNav.js — Shared prev/next nav-button behavior for horizontal card
 * carousels: scroll-by-page on click, auto-hide at either end, and re-checks
 * on track resize (images loading, panel opening from closed/display:none).
 *
 * Usage: put the track inside a `.ws-carousel` element (that class supplies
 * position:relative + the side gutters the nav buttons sit in), append the
 * track's cards, then call attachCarouselNav(carouselEl, trackEl).
 */

const SCROLL_STEP = 260;

export function attachCarouselNav(carousel, track, { step = SCROLL_STEP } = {}) {
  const prev = document.createElement("button");
  prev.type = "button";
  prev.className = "ws-carousel-nav ws-carousel-prev";
  prev.setAttribute("aria-label", "Previous");
  prev.innerHTML = "&#10094;";

  const next = document.createElement("button");
  next.type = "button";
  next.className = "ws-carousel-nav ws-carousel-next";
  next.setAttribute("aria-label", "Next");
  next.innerHTML = "&#10095;";

  const updateNavVisibility = () => {
    const { scrollLeft, scrollWidth, clientWidth } = track;
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
    const overflow = scrollWidth > clientWidth + 2;
    prev.style.display = overflow && !atStart ? "" : "none";
    next.style.display = overflow && !atEnd ? "" : "none";
  };

  prev.addEventListener("click", () => {
    track.scrollBy({ left: -step, behavior: "smooth" });
  });
  next.addEventListener("click", () => {
    track.scrollBy({ left: step, behavior: "smooth" });
  });
  track.addEventListener("scroll", updateNavVisibility, { passive: true });

  carousel.appendChild(prev);
  carousel.appendChild(next);

  // Start at the true left so the first card's margin/glow is visible. Some
  // browsers snap past leading padding on first layout otherwise.
  const settleStart = () => {
    track.scrollLeft = 0;
    updateNavVisibility();
  };
  settleStart();
  requestAnimationFrame(settleStart);

  // On a page reload, chat-history.js restores the transcript while
  // #ws-chat-panel is still display:none (closed by default) — track's
  // clientWidth/scrollWidth both read 0, so the checks above wrongly
  // conclude there's no overflow and hide the nav arrows for good, even
  // after the panel opens and the cards visibly overflow. ResizeObserver
  // fires again once the track gets a real box (panel open, window resize,
  // images finishing load), so it self-corrects.
  if (typeof ResizeObserver !== "undefined") {
    const ro = new ResizeObserver(updateNavVisibility);
    ro.observe(track);
  }
}
